import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { getHomePageModulesByType } from 'lib/homepage';
import { PageContent, PageHeader } from 'components/page';
import {
  HomeActiveCourses,
  HomeActivePaths,
  HomeAdminContent,
  HomeBannerImage,
  HomeDetailsView,
  HomeHeader
} from '.';

import type { GetHomePageModulesRes } from 'shared-types/responses';
import type { HomeDetailsViewType } from './HomeDetailsView';

export type HomeModuleProps = {
  defaultTitle: string;
  limit: number;
  module: GetHomePageModulesRes['modules'][0];
  spacing: number;
};

const Home = () => {
  const { messages, serverConfig, user } = useReduxState();

  const router = useRouter();

  // if someone navigated directly to a detailed view, we don't want to
  // render the main content immediately
  const [renderMainContent, setRenderMainContent] = useState<boolean>(
    !router.query.view
  );

  useEffect(() => {
    if (!renderMainContent && !router.query.view) setRenderMainContent(true);
  }, [router.query.view]);

  const { data, loading } = useApiData<GetHomePageModulesRes>({
    url: '/admin/home-page',
    onlyIf: Boolean(user) && renderMainContent
  });

  const handleClearDetails = () => {
    const query = JSON.parse(JSON.stringify(router.query));
    delete query.view;

    router.push({ query }, undefined, { shallow: true });
  };

  const modules = getHomePageModulesByType({ messages, serverConfig });

  const detailsView = (router.query.view || '') as HomeDetailsViewType;
  const limitItemsTo = 4;
  const moduleSpacing = 80;

  return (
    <div className="home">
      <HomeBannerImage />

      {detailsView ? (
        <>
          <PageHeader
            icon="arrow-left"
            iconOnClick={() => handleClearDetails()}
            title={messages.headerHome}
          />

          <HomeDetailsView type={detailsView} />
        </>
      ) : null}

      {renderMainContent ? (
        <div style={{ display: detailsView ? 'none' : 'block' }}>
          <HomeHeader />

          <PageContent>
            <div className="modules">
              {user?.roles.includes('admin') &&
              !serverConfig.hideCoursesFromUI ? (
                <HomeAdminContent />
              ) : null}

              {serverConfig.homePageShowMyCourses &&
              !serverConfig.hideCoursesFromUI ? (
                <div className="module">
                  <HomeActiveCourses />
                </div>
              ) : null}

              {serverConfig.homePageShowMyPaths &&
              !serverConfig.hidePathsFromUI ? (
                <HomeActivePaths spacing={moduleSpacing} />
              ) : null}

              {!loading && data?.modules?.length
                ? data.modules
                    .filter((module) => module.type in modules) // filter out disabled modules
                    .map((module, index) => {
                      const { HomeComponent, title } = modules[module.type];

                      return (
                        <HomeComponent
                          key={index}
                          defaultTitle={title}
                          limit={limitItemsTo}
                          module={module}
                          spacing={moduleSpacing}
                        />
                      );
                    })
                : null}
            </div>
          </PageContent>
        </div>
      ) : null}

      <style jsx>{`
        .modules {
          display: flex;
          flex-direction: column;
          margin-top: 20px;
        }
        .module:not(:first-child) {
          margin-top: ${moduleSpacing}px;
        }
      `}</style>
    </div>
  );
};

export default Home;
